import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboard';
import { User } from '../types/auth';

// Query keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  userDetails: (userId: string) =>
    [...dashboardKeys.all, 'user-details', userId] as const,
  shipmentStatus: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'shipment-status',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  shipmentCharges: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'shipment-charges',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  shipmentCount: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'shipment-count',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  courierDistribution: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'courier-distribution',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  zoneDistribution: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'zone-distribution',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  regionDistribution: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'region-distribution',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  cityDistribution: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'city-distribution',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  RTOcityDistribution: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'rto-city-distribution',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  ModeAndSurface: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'mode-surface',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
  rtoAnalysis: (
    userId: string,
    fromDate: string,
    toDate: string,
    range?: string,
    type?: string
  ) =>
    [
      ...dashboardKeys.all,
      'rto-analysis',
      userId,
      fromDate,
      toDate,
      range,
      type,
    ] as const,
};
// Dashboard Hooks
export const useShipmentStatusCount = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.shipmentStatus(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getShipmentStatusCount(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useShipmentCount = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.shipmentCount(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () => dashboardService.getShipmentCount(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useCourierDistribution = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.courierDistribution(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getCourierDistribution(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

export const useZoneDistribution = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.zoneDistribution(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getZoneDistribution(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

export const useRegionDistribution = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.regionDistribution(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getRegionDistribution(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

export const useShipmentCharges = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.shipmentCharges(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: async () => {
      const res = await dashboardService.getShipmentCharges(
        userId,
        fromDate,
        toDate
      );
      return res as any; // 👈 as you asked, no interface
    },
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};
export const useCityDistribution = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.cityDistribution(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getCityDistributionCount(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};
export const useRTOCityDistribution = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.RTOcityDistribution(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getRTOCityDistributionCount(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};
export const useModeAndSurface = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.ModeAndSurface(
      userId,
      fromDate,
      toDate,
      range,
      type
    ),
    queryFn: () =>
      dashboardService.getModeAndTypeDistributions(userId, fromDate, toDate),
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

export const useRTOAnalysis = (
  userId: string,
  fromDate: string,
  toDate: string,
  range?: string,
  type?: string
) => {
  return useQuery({
    queryKey: dashboardKeys.rtoAnalysis(userId, fromDate, toDate, range, type),
    queryFn: async () => {
      const data = await dashboardService.getRTOAnalysis(
        userId,
        fromDate,
        toDate
      );
      return data;
    },
    enabled: !!userId && !!fromDate && !!toDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};
